/**
 * Static data loading for production deployment
 * In production: loads from /data/*.json (exported from DB)
 * In development: falls back to API calls
 */

const IS_PRODUCTION = process.env.NODE_ENV === 'production';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

interface JobsResponse {
  data: any[];
  total: number;
  exportedAt?: string;
}

interface CompaniesResponse {
  data: any[];
  total: number;
  exportedAt?: string;
}

interface TagsResponse {
  curated: Record<string, string[]>;
  used: Record<string, string[]>;
  techStack: string[];
  exportedAt?: string;
}

interface MetaResponse {
  jobCount: number;
  companyCount: number;
  exportedAt: string;
}

// Cache for static data
let jobsCache: JobsResponse | null = null;
let companiesCache: CompaniesResponse | null = null;
let tagsCache: TagsResponse | null = null;

/**
 * Load jobs - from static JSON in production, API in development
 */
export async function loadJobs(token?: string): Promise<JobsResponse> {
  if (IS_PRODUCTION) {
    if (!jobsCache) {
      const response = await fetch('/data/jobs.json');
      jobsCache = await response.json();
    }
    return jobsCache!;
  }

  // Development: use API (max limit is 100)
  const response = await fetch(`${API_URL}/jobs?limit=100`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  return response.json();
}

/**
 * Load companies - from static JSON in production, API in development
 */
export async function loadCompanies(): Promise<CompaniesResponse> {
  if (IS_PRODUCTION) {
    if (!companiesCache) {
      const response = await fetch('/data/companies.json');
      companiesCache = await response.json();
    }
    return companiesCache!;
  }

  // Development: use API (max limit is 100)
  const response = await fetch(`${API_URL}/companies?limit=100`);
  return response.json();
}

/**
 * Load tags - from static JSON in production
 */
export async function loadTags(): Promise<TagsResponse | null> {
  if (IS_PRODUCTION) {
    if (!tagsCache) {
      const response = await fetch('/data/tags.json');
      tagsCache = await response.json();
    }
    return tagsCache;
  }
  return null; // In dev, tags come from curated list in code
}

/**
 * Load metadata
 */
export async function loadMeta(): Promise<MetaResponse | null> {
  if (IS_PRODUCTION) {
    const response = await fetch('/data/meta.json');
    return response.json();
  }
  return null;
}

/**
 * Check if running in static/production mode
 */
export function isStaticMode(): boolean {
  return IS_PRODUCTION;
}

/**
 * Clear cache (useful for testing)
 */
export function clearCache(): void {
  jobsCache = null;
  companiesCache = null;
  tagsCache = null;
}
